<script setup>
import { ref, computed } from 'vue'
import { Wrench, Plus, Minus, RotateCcw } from 'lucide-vue-next'
import { usePlanStore, TIER_ORDER } from '@/stores/planStore'
import { useQuotaStore } from '@/stores/quotaStore'
import { useDocumentsStore } from '@/stores/documentsStore'

const plan = usePlanStore()
const quota = useQuotaStore()
const documents = useDocumentsStore()

const open = ref(false)

// Visible only in dev OR mock mode. Once real Cognito + Polar are wired,
// this disappears in production builds.
const isVisible = computed(() => import.meta.env.DEV || plan.isMockMode)

function cycleTier() {
  const idx = TIER_ORDER.indexOf(plan.tier)
  const next = TIER_ORDER[(idx + 1) % TIER_ORDER.length]
  plan.setTier(next)
}

function bumpUsage(delta) {
  quota.setUsed(quota.pagesUsed + delta)
}

function reseed() {
  documents.reseedDemo()
  quota.setUsed(0)
}
</script>

<template>
  <div v-if="isVisible" class="fixed bottom-4 right-4 z-40 select-none">
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="open" class="card-surface mb-2 w-64 p-3 shadow-xl">
        <p class="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          <Wrench class="h-3 w-3" />
          Dev tools
        </p>

        <div class="space-y-3">
          <div>
            <p class="mb-1 text-xs text-slate-500 dark:text-slate-400">Tier</p>
            <button type="button" class="btn-secondary w-full" @click="cycleTier">
              {{ plan.definition.label }} → next
            </button>
          </div>

          <div>
            <p class="mb-1 text-xs text-slate-500 dark:text-slate-400">
              Quota: {{ quota.pagesUsed }} / {{ quota.pagesLimit }} pg · {{ quota.overagePagesPeriod }} ov
            </p>
            <div class="grid grid-cols-2 gap-2">
              <button type="button" class="btn-secondary" @click="bumpUsage(-1)">
                <Minus class="h-4 w-4" />
              </button>
              <button type="button" class="btn-secondary" @click="bumpUsage(1)">
                <Plus class="h-4 w-4" />
              </button>
            </div>
          </div>

          <div>
            <p class="mb-1 text-xs text-slate-500 dark:text-slate-400">Documents</p>
            <button type="button" class="btn-secondary w-full" @click="reseed">
              <RotateCcw class="h-4 w-4" />
              Reseed demo set
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <button
      type="button"
      class="flex h-12 w-12 items-center justify-center rounded-full bg-slate-900 text-white shadow-lg transition hover:bg-slate-800 dark:bg-slate-700"
      :aria-label="open ? 'Close dev tools' : 'Open dev tools'"
      @click="open = !open"
    >
      <Wrench class="h-5 w-5" />
    </button>
  </div>
</template>
